import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { auctionHouseApi } from '@/api/auctionHouseApi';
import { i18n, type UILanguageCode } from "@/locales";

const LAST_SELECTED_AUCTION_HOUSE_KEY = 'lastSelectedAuctionHouseSlug';

export const useCurrentAuctionHouse = () => {
    const { slug } = useParams<{ slug?: string }>();

    // If slug exists, use current behavior
    const { data: auctionHouse, isLoading: isLoadingBySlug, error: errorBySlug } = useQuery({
        queryKey: ['auctionHouse', slug],
        queryFn: () => auctionHouseApi.getAuctionHouse(slug!),
        enabled: !!slug,
    });

    // If no slug, fetch all and use last selected or first
    const { data: allAuctionHouses, isLoading: isLoadingAll, error: errorAll } = useQuery({
        queryKey: ['auctionHouses', 'admin'],
        queryFn: auctionHouseApi.getUserAuctionHouses,
        enabled: !slug,
    });

    // Store current slug in localStorage when navigating to a specific auction house
    useEffect(() => {
        if (slug) {
            localStorage.setItem(LAST_SELECTED_AUCTION_HOUSE_KEY, slug);
        }
    }, [slug]);

    // When no slug, try to use last selected auction house, fallback to first
    const getFallbackAuctionHouse = () => {
        if (!allAuctionHouses) return undefined;

        const lastSelectedSlug = localStorage.getItem(LAST_SELECTED_AUCTION_HOUSE_KEY);
        if (lastSelectedSlug) {
            const lastSelected = allAuctionHouses.find(ah => ah.slug === lastSelectedSlug);
            if (lastSelected) return lastSelected;
        }

        return allAuctionHouses[0];
    };

    const fallbackAuctionHouse = slug ? undefined : getFallbackAuctionHouse();
    const currentAuctionHouse = slug ? auctionHouse : fallbackAuctionHouse;

    const currentLang = i18n.language as UILanguageCode;
    const fallbackLang = currentLang === 'he' ? 'en' : 'he';

    const translatedName =
        currentAuctionHouse?.content[currentLang]?.name ||
        currentAuctionHouse?.content[fallbackLang]?.name ||
        '';

    return {
        auctionHouse: currentAuctionHouse,
        translatedName,
        isLoading: slug ? isLoadingBySlug : isLoadingAll,
        slug: slug || fallbackAuctionHouse?.slug,
        auctionHouseId: currentAuctionHouse?.id,
        error: slug ? errorBySlug : errorAll,
    };
};
